import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import DashboardLayout from '../../components/layout/DashboardLayout';
import DataTable from '../../components/common/DataTable';
import { activityApi } from '../../api/activity.api';

const actionLabels = {
  login: 'Login',
  logout: 'Logout',
  page_visit: 'Page Visit',
  create: 'Create',
  update: 'Update',
  delete: 'Delete',
  password_reset: 'Password Reset',
  send_reset_email: 'Send Reset Email'
};

const actionColors = {
  login: 'bg-green-100 text-green-800',
  logout: 'bg-gray-100 text-gray-800',
  page_visit: 'bg-blue-100 text-blue-800',
  create: 'bg-purple-100 text-purple-800',
  update: 'bg-yellow-100 text-yellow-800',
  delete: 'bg-red-100 text-red-800',
  password_reset: 'bg-orange-100 text-orange-800',
  send_reset_email: 'bg-indigo-100 text-indigo-800'
};

export default function AdminActivityPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [adminStats, setAdminStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    loadAdminStats();
  }, [id]);

  useEffect(() => {
    loadActivities();
  }, [id, pagination.page]);

  const loadAdminStats = async () => {
    try {
      setLoading(true);
      const response = await activityApi.getAdminStats(id);
      setAdminStats(response.data);
    } catch (error) {
      console.error('Failed to load admin stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadActivities = async () => {
    try {
      setActivitiesLoading(true);
      const response = await activityApi.getByAdmin(id, {
        page: pagination.page,
        limit: pagination.limit
      });
      setActivities(response.data.data || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || 0,
        pages: response.data.pagination?.pages || 0
      }));
    } catch (error) {
      console.error('Failed to load activities:', error);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString();
  };

  const getRoleBadgeColor = (roleName) => {
    switch (roleName) {
      case 'admin':
        return 'bg-blue-100 text-blue-800';
      case 'basic':
        return 'bg-gray-100 text-gray-800';
      case 'superadmin':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const columns = [
    {
      key: 'action',
      label: 'Action',
      render: (row) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${actionColors[row.action] || 'bg-gray-100 text-gray-800'}`}>
          {actionLabels[row.action] || row.action}
        </span>
      )
    },
    {
      key: 'page',
      label: 'Page / Resource',
      render: (row) => (
        <div>
          {row.page && (
            <span className="text-gray-900">{row.page}</span>
          )}
          {row.resource && (
            <span className="text-gray-600">
              {row.resource}
              {row.resourceName && `: ${row.resourceName}`}
            </span>
          )}
          {!row.page && !row.resource && (
            <span className="text-gray-400">-</span>
          )}
        </div>
      )
    },
    {
      key: 'details',
      label: 'Details',
      render: (row) => (
        <div className="max-w-xs truncate text-sm text-gray-500">
          {row.details?.message || (row.details ? JSON.stringify(row.details) : '-')}
        </div>
      )
    },
    {
      key: 'ipAddress',
      label: 'IP Address',
      render: (row) => (
        <span className="text-sm text-gray-500">{row.ipAddress || '-'}</span>
      )
    },
    {
      key: 'createdAt',
      label: 'Time',
      render: (row) => (
        <span className="text-sm text-gray-500">{formatDate(row.createdAt)}</span>
      )
    }
  ];

  if (loading) {
    return (
      <DashboardLayout title="Admin Activity">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!adminStats) {
    return (
      <DashboardLayout title="Admin Activity">
        <div className="text-center py-12">
          <p className="text-gray-500">Admin not found</p>
          <button
            onClick={() => navigate('/activity')}
            className="mt-4 text-primary-600 hover:text-primary-900"
          >
            Back to Activity Log
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={`Activity: ${adminStats.admin?.name}`}>
      {/* Back button */}
      <button
        onClick={() => navigate('/activity')}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeftIcon className="h-5 w-5 mr-2" />
        Back to Activity Log
      </button>

      {/* Admin Info & Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm font-medium text-gray-500">Role</div>
          <div className="mt-1">
            <span className={`px-2 py-1 text-sm font-medium rounded-full capitalize ${getRoleBadgeColor(adminStats.admin?.role?.name)}`}>
              {adminStats.admin?.role?.name || 'Unknown'}
            </span>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm font-medium text-gray-500">Last Login</div>
          <div className="mt-1 text-lg font-semibold text-gray-900">
            {formatDate(adminStats.lastLogin)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm font-medium text-gray-500">Most Visited Page</div>
          <div className="mt-1 text-lg font-semibold text-gray-900 truncate">
            {adminStats.mostVisitedPage || 'None'}
          </div>
          {adminStats.mostVisitedPageCount > 0 && (
            <div className="text-sm text-gray-500">
              {adminStats.mostVisitedPageCount} visits
            </div>
          )}
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm font-medium text-gray-500">Total Activities</div>
          <div className="mt-1 text-lg font-semibold text-gray-900">
            {adminStats.totalActivities?.toLocaleString() || 0}
          </div>
          <div className="text-sm text-gray-500">
            {adminStats.loginCount || 0} logins
          </div>
        </div>
      </div>

      {/* Admin Info Card */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex items-center">
          <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
            <span className="text-xl font-semibold text-primary-600">
              {adminStats.admin?.name?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-semibold text-gray-900">{adminStats.admin?.name}</h3>
            <p className="text-sm text-gray-500">{adminStats.admin?.email}</p>
          </div>
        </div>
      </div>

      {/* Activity History */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Activity History</h3>
      </div>

      <DataTable
        columns={columns}
        data={activities}
        loading={activitiesLoading}
        emptyMessage="No activity found for this admin"
        pagination={pagination}
        onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
      />
    </DashboardLayout>
  );
}
